import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskCategoryItem, TaskItem, TaskStatusDefinition } from '../types/Task';
import {
  buildBarChartSvg,
  buildChartRows,
  buildPieChartSegments,
  buildPieChartSvg,
  buildTableChartSvg,
  ChartGroupField,
  ChartView,
  sanitizeChartFileName,
  svgMarkupToPngBlob,
  triggerBlobDownload,
} from '../utils/taskChart';
import styles from './TaskChartModal.module.css';

type TaskChartModalProps = {
  isOpen: boolean;
  onClose: () => void;
  tasks: TaskItem[];
  employeeLookup: Record<string, { name: string; profileImageUrl?: string; colorPreference?: string }>;
  statusOptions: TaskStatusDefinition[];
  categoryOptions?: TaskCategoryItem[];
  onError: (message: string) => void;
};

export const TaskChartModal: React.FC<TaskChartModalProps> = ({
  isOpen,
  onClose,
  tasks,
  employeeLookup,
  statusOptions,
  categoryOptions = [],
  onError,
}) => {
  const { t } = useTranslation(['tasks', 'common']);
  const [chartGroupField, setChartGroupField] = useState<ChartGroupField>('assignee');
  const [chartView, setChartView] = useState<ChartView>('bar');
  const [chartExporting, setChartExporting] = useState<null | 'csv' | 'png'>(null);

  const chartRows = useMemo(
    () => buildChartRows(tasks, chartGroupField, employeeLookup, statusOptions, t, categoryOptions),
    [categoryOptions, chartGroupField, employeeLookup, statusOptions, t, tasks]
  );
  const maxChartCount = useMemo(() => chartRows.reduce((max, item) => Math.max(max, item.count), 0), [chartRows]);
  const totalChartCount = useMemo(() => chartRows.reduce((sum, item) => sum + item.count, 0), [chartRows]);
  const pieChartSegments = useMemo(() => buildPieChartSegments(chartRows, totalChartCount), [chartRows, totalChartCount]);

  const chartTitle = useMemo(() => {
    const groupLabel = chartGroupField === 'assignee'
      ? t('assigneeLabel', { ns: 'tasks' })
      : chartGroupField === 'status'
        ? t('statusLabel', { ns: 'tasks' })
        : chartGroupField === 'priority'
          ? t('priorityLabel', { ns: 'tasks' })
          : t('categoryLabel', { ns: 'tasks', defaultValue: 'Category' });

    return t('chartTitleTemplate', {
      ns: 'tasks',
      groupLabel,
      defaultValue: `Work items by ${groupLabel}`,
    });
  }, [chartGroupField, t]);

  const handleExportChartCsv = useCallback(() => {
    setChartExporting('csv');
    try {
      const header = ['Value', 'Count', 'Share'];
      const lines = chartRows.map(row => {
        const share = totalChartCount > 0 ? `${Math.round((row.count / totalChartCount) * 100)}%` : '0%';
        return [row.label, String(row.count), share]
          .map(value => `"${String(value).replace(/"/g, '""')}"`)
          .join(',');
      });
      const csv = [header.join(','), ...lines].join('\r\n');
      triggerBlobDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${sanitizeChartFileName(chartTitle)}.csv`);
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setChartExporting(null);
    }
  }, [chartRows, chartTitle, onError, t, totalChartCount]);

  const handleExportChartPng = useCallback(async () => {
    setChartExporting('png');
    try {
      const exportPayload = chartView === 'pie'
        ? buildPieChartSvg(pieChartSegments, totalChartCount, chartTitle)
        : chartView === 'table'
          ? buildTableChartSvg(chartRows, totalChartCount, chartTitle)
          : buildBarChartSvg(chartRows, maxChartCount, chartTitle);
      const pngBlob = await svgMarkupToPngBlob(exportPayload.markup, exportPayload.width, exportPayload.height);
      triggerBlobDownload(pngBlob, `${sanitizeChartFileName(chartTitle)}.png`);
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setChartExporting(null);
    }
  }, [chartRows, chartTitle, chartView, maxChartCount, onError, pieChartSegments, t, totalChartCount]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('viewAsChart', { ns: 'tasks', defaultValue: 'View as chart' })} onClick={onClose}>
      <div className={styles.chartModalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('viewAsChart', { ns: 'tasks', defaultValue: 'View as chart' })}</h3>
          <button className={styles.modalCloseBtn} type="button" onClick={onClose} aria-label={t('close', { ns: 'common' })}>×</button>
        </div>

        <div className={styles.chartModalBody}>
          <div className={styles.chartSettings}>
            <label className={styles.chartField}>
              <span>{t('column', { ns: 'tasks', defaultValue: 'Column' })}</span>
              <select
                className={`text-input ${styles.chartSelect}`}
                value={chartGroupField}
                onChange={(event) => setChartGroupField(event.target.value as ChartGroupField)}
              >
                <option value="assignee">{t('assigneeLabel', { ns: 'tasks' })}</option>
                <option value="status">{t('statusLabel', { ns: 'tasks' })}</option>
                <option value="priority">{t('priorityLabel', { ns: 'tasks' })}</option>
                <option value="category">{t('categoryLabel', { ns: 'tasks', defaultValue: 'Category' })}</option>
              </select>
            </label>
            <div className={styles.chartTypeGroup}>
              <span className={styles.chartTypeLabel}>{t('chartType', { ns: 'tasks', defaultValue: 'Chart type' })}</span>
              <div className={styles.chartTypeButtons}>
                <button
                  type="button"
                  className={`${styles.chartTypeButton} ${chartView === 'bar' ? styles.chartTypeButtonActive : ''}`}
                  onClick={() => setChartView('bar')}
                  aria-pressed={chartView === 'bar'}
                  title={t('barChart', { ns: 'tasks', defaultValue: 'Bar chart' })}
                >
                  <span className={styles.chartTypeIconBar} aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.chartTypeButton} ${chartView === 'pie' ? styles.chartTypeButtonActive : ''}`}
                  onClick={() => setChartView('pie')}
                  aria-pressed={chartView === 'pie'}
                  title={t('pieChart', { ns: 'tasks', defaultValue: 'Pie chart' })}
                >
                  <span className={styles.chartTypeIconPie} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`${styles.chartTypeButton} ${chartView === 'table' ? styles.chartTypeButtonActive : ''}`}
                  onClick={() => setChartView('table')}
                  aria-pressed={chartView === 'table'}
                  title={t('tableView', { ns: 'tasks', defaultValue: 'Table view' })}
                >
                  <span className={styles.chartTypeIconTable} aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span />
                  </span>
                </button>
              </div>
            </div>
            <div className={styles.chartSummary}>
              {t('chartSummary', {
                ns: 'tasks',
                count: tasks.length,
                defaultValue: `${tasks.length} filtered work items`,
              })}
            </div>
            <div className={styles.chartExportActions}>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={handleExportChartCsv}
                disabled={chartExporting !== null || chartRows.length === 0}
              >
                {chartExporting === 'csv'
                  ? t('saving', { ns: 'common' })
                  : t('exportCsv', { ns: 'tasks', defaultValue: 'Export CSV' })}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => void handleExportChartPng()}
                disabled={chartExporting !== null || chartRows.length === 0}
              >
                {chartExporting === 'png'
                  ? t('saving', { ns: 'common' })
                  : t('downloadPng', { ns: 'tasks', defaultValue: 'Download PNG' })}
              </button>
            </div>
          </div>

          {chartRows.length === 0 ? (
            <div className={styles.emptyState}>{t('empty', { ns: 'tasks' })}</div>
          ) : chartView === 'pie' ? (
            <div className={styles.chartPieLayout}>
              <div className={styles.chartPieCard}>
                <svg viewBox="0 0 120 120" className={styles.chartPieSvg} role="img" aria-label={t('pieChart', { ns: 'tasks', defaultValue: 'Pie chart' })}>
                  <circle cx="60" cy="60" r="42" className={styles.chartPieBase} />
                  {pieChartSegments.map(segment => (
                    <circle
                      key={segment.label}
                      cx="60"
                      cy="60"
                      r="42"
                      className={styles.chartPieSegment}
                      style={{ stroke: segment.color, strokeDasharray: segment.dash, strokeDashoffset: segment.dashOffset }}
                    />
                  ))}
                </svg>
                <div className={styles.chartPieCenter}>
                  <strong>{totalChartCount}</strong>
                  <span>{t('items', { ns: 'tasks', defaultValue: 'items' })}</span>
                </div>
              </div>
              <div className={styles.chartLegend}>
                {pieChartSegments.map(segment => (
                  <div key={segment.label} className={styles.chartLegendRow}>
                    <span className={styles.chartLegendDot} style={{ backgroundColor: segment.color }} aria-hidden="true" />
                    <div className={styles.chartLegendLabel} title={segment.label}>{segment.label}</div>
                    <div className={styles.chartLegendMeta}>{Math.round(segment.percentage * 100)}%</div>
                    <div className={styles.chartLegendMeta}>{segment.count}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : chartView === 'table' ? (
            <div className={styles.chartTable}>
              <div className={`${styles.chartTableRow} ${styles.chartTableHeader}`}>
                <span>{t('value', { ns: 'common', defaultValue: 'Value' })}</span>
                <span>{t('count', { ns: 'tasks', defaultValue: 'Count' })}</span>
                <span>{t('share', { ns: 'tasks', defaultValue: 'Share' })}</span>
              </div>
              {chartRows.map(item => (
                <div key={item.label} className={styles.chartTableRow}>
                  <span className={styles.chartTableValue} title={item.label}>{item.label}</span>
                  <span>{item.count}</span>
                  <span>{totalChartCount > 0 ? `${Math.round((item.count / totalChartCount) * 100)}%` : '0%'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.chartRows}>
              {chartRows.map(item => (
                <div key={item.label} className={styles.chartRow}>
                  <div className={styles.chartLabel} title={item.label}>{item.label}</div>
                  <div className={styles.chartTrack}>
                    <div
                      className={styles.chartBar}
                      style={{ width: `${Math.max(8, Math.round((item.count / Math.max(1, maxChartCount)) * 100))}%` }}
                    />
                  </div>
                  <div className={styles.chartValue}>{item.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskChartModal;
